import React, { useState } from 'react';
import { Row, Col, Form, Table, Alert, Badge, Button, Card } from 'react-bootstrap';
import { FaPlus, FaTrash, FaUsers, FaUpload, FaPlay, FaTimes } from 'react-icons/fa';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

const NewStudentsSelection = ({ workflowData, setWorkflowData, error, setError }) => {
  const [rawStudentText, setRawStudentText] = useState("");
  const [showVideo, setShowVideo] = useState(false);

  const handleParseStudents = () => {
    if (!rawStudentText.trim()) return;
    parseStudentNames();
    setRawStudentText("");
  };

  const parseStudentNames = () => {
    if (!rawStudentText.trim()) {
      setWorkflowData(prev => ({ ...prev, newStudents: [] }));
      return;
    }

    let names = [];
    
    if (rawStudentText.includes('\n')) {
      names = rawStudentText.split('\n');
    } else if (rawStudentText.includes(', ')) {
      names = rawStudentText.split(', ');
    } else if (rawStudentText.includes(',')) {
      names = rawStudentText.split(',');
    } else {
      names = [rawStudentText];
    }

    const processedStudents = names
      .map(name => name.trim())
      .filter(name => name.length > 0)
      .map((name, index) => {
        const nameParts = name.split(' ').filter(part => part.trim());
        let firstName = '';
        let lastName = '';

        if (nameParts.length === 1) {
          firstName = nameParts[0];
        } else if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        }

        return {
          id: `temp-${Date.now()}-${index}`,
          originalName: name,
          firstName: firstName,
          lastName: lastName,
          displayName: name,
          gender: '',
          age: null,
          sen: false,
          esl: false,
          asd: false,
          gdd: false,
          send: false,
          country: '',
          currentGrade: '',
          isSpecialEd: false,
          isValid: firstName.length > 0,
          validationError: firstName.length === 0 ? "Name cannot be empty" : ""
        };
      });

    setWorkflowData(prev => {
      const existingStudents = prev.newStudents || [];
      const newStudents = [...existingStudents, ...processedStudents];
      return { ...prev, newStudents: newStudents };
    });
  };

  const extractDataFromExcel = (sheetData) => {
    const studentNames = [];
    let nameStarts = false;
    
    sheetData.forEach((element) => {
      element.forEach((rowString) => {
        if (typeof rowString === 'string') {
          if (rowString.includes("function")) {
            nameStarts = false;
          } else if (nameStarts) {
            const studentName = rowString
              .replace(/<style[^>]*>.*?<\/style>/gs, "")
              .replace(/<script[^>]*>.*?<\/script>/gs, "")
              .replace(/<[^>]+>/g, "")
              .replace(/[\r\n]+ +/g, "")
              .trim();

            if (studentName) studentNames.push(studentName);
          }

          if (rowString.includes("Name")) {
            nameStarts = true;
          }
        }
      });
    });
    
    return studentNames;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileType = file.type;
    const fileName = file.name.toLowerCase();

    if (
      fileType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileType === "application/vnd.ms-excel" ||
      fileName.endsWith('.xlsx') ||
      fileName.endsWith('.xls')
    ) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const xlsxStudents = extractDataFromExcel(sheetData);
          addStudentsFromImport(xlsxStudents);
        } catch (err) {
          setError("Failed to read Excel file. Please check the file format.");
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (fileType === "text/csv" || fileName.endsWith('.csv')) {
      Papa.parse(file, {
        complete: (results) => {
          try {
            const csvStudents = results.data
              .map((row) => {
                if (Array.isArray(row)) {
                  const [firstName, lastName] = row;
                  return `${firstName || ""} ${lastName || ""}`.trim();
                } else if (typeof row === 'object' && row !== null) {
                  const firstKey = Object.keys(row)[0];
                  return row[firstKey] || '';
                }
                return '';
              })
              .filter((name) => name.trim() !== "" && name.toLowerCase() !== "name");
              
            addStudentsFromImport(csvStudents);
          } catch (err) {
            setError("Failed to parse CSV file. Please check the file format.");
          }
        },
        header: true,
        skipEmptyLines: true
      });
    } else {
      setError("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
    }

    event.target.value = '';
  };

  const addStudentsFromImport = (studentNames) => {
    const processedStudents = studentNames
      .filter(name => name && name.trim().length > 0)
      .map((name, index) => {
        const cleanName = name.trim();
        const nameParts = cleanName.split(' ').filter(part => part.trim());
        let firstName = '';
        let lastName = '';

        if (nameParts.length === 1) {
          firstName = nameParts[0];
        } else if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        }

        return {
          id: `import-${Date.now()}-${index}`,
          originalName: cleanName,
          firstName: firstName,
          lastName: lastName,
          displayName: cleanName,
          gender: '',
          age: null,
          sen: false,
          esl: false,
          asd: false,
          gdd: false,
          send: false,
          country: '',
          currentGrade: '',
          isSpecialEd: false,
          isValid: firstName.length > 0,
          validationError: firstName.length === 0 ? "Name cannot be empty" : ""
        };
      });

    setWorkflowData(prev => {
      const existingStudents = prev.newStudents || [];
      const newStudents = [...existingStudents, ...processedStudents];
      return { ...prev, newStudents: newStudents };
    });

    setRawStudentText("");
  };

  const updateStudentField = (studentId, field, value) => {
    setWorkflowData(prev => ({
      ...prev,
      newStudents: prev.newStudents.map(student => 
        student.id === studentId 
          ? { ...student, [field]: value }
          : student
      )
    }));
  };

  const removeStudent = (studentId) => {
    setWorkflowData(prev => ({
      ...prev,
      newStudents: prev.newStudents.filter(student => student.id !== studentId)
    }));
  };

  const addManualStudent = () => {
    const newStudent = {
      id: `manual-${Date.now()}`,
      originalName: "",
      firstName: "",
      lastName: "",
      displayName: "",
      gender: '',
      age: null,
      sen: false,
      esl: false,
      asd: false,
      gdd: false,
      send: false,
      country: '',
      currentGrade: '',
      isSpecialEd: false,
      isValid: false,
      validationError: "Please enter a name"
    };
    setWorkflowData(prev => ({
      ...prev,
      newStudents: [...(prev.newStudents || []), newStudent]
    }));
  };

  const updateManualStudentName = (studentId, firstName, lastName) => {
    const displayName = `${firstName} ${lastName}`.trim();
    const isValid = firstName.trim().length > 0;
    
    setWorkflowData(prev => ({
      ...prev,
      newStudents: prev.newStudents.map(student => 
        student.id === studentId 
          ? { 
              ...student, 
              firstName: firstName,
              lastName: lastName,
              displayName: displayName,
              originalName: displayName,
              isValid: isValid,
              validationError: isValid ? "" : "First name is required"
            }
          : student
      )
    }));
  };

  const clearAllStudents = () => {
    setWorkflowData(prev => ({ ...prev, newStudents: [] }));
    setRawStudentText("");
  };

  const newStudents = workflowData.newStudents || [];
  const validStudentsCount = newStudents.filter(s => s.isValid).length;

  return (
    <div>
      <h5>Add New Students</h5>
      <p className="text-muted">Add new students to the created classes.</p>
      
      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="h-100">
        {/* Column 1: Input Options */}
        <Col md={4} className="border-end pe-3">
          <div className="mb-4">
            <h6 className="mb-3">Add Students</h6>
            
            {/* File Upload Section */}
            <Card className="mb-3">
              <Card.Body className="p-3">
                <h6 className="card-title mb-2">
                  <FaUpload className="me-2" />
                  Import from File
                </h6>
                <Form.Group className="mb-2">
                  <Form.Control
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    size="sm"
                  />
                  <Form.Text className="text-muted">
                    CSV or Excel files from Aladdin, Arbor, iSAMS, etc.
                  </Form.Text>
                </Form.Group>
                
                {/* Video Guide Toggle */}
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => setShowVideo(!showVideo)}
                  className="w-100"
                >
                  {showVideo ? (
                    <>
                      <FaTimes className="me-1" />
                      Hide Guide
                    </>
                  ) : (
                    <>
                      <FaPlay className="me-1" />
                      Watch Guide
                    </>
                  )}
                </Button>
                
                {showVideo && (
                  <div className="mt-3">
                    <div className="ratio ratio-16x9">
                      <iframe
                        src="https://www.youtube.com/embed/v5F5Hj-q2b4?si=1e5dsf7Wpiy5hdm6"
                        title="File Import Guide"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Text Input Section */}
            <Card className="mb-3">
              <Card.Body className="p-3">
                <h6 className="card-title mb-2">Paste Student Names</h6>
                <Form.Group className="mb-3">
                  <Form.Control
                    as="textarea"
                    rows={8}
                    placeholder={`Paste student names here...\n\nExample:\nJohn Smith\nMary Johnson\nPeter O'Connor`}
                    value={rawStudentText}
                    onChange={(e) => setRawStudentText(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Names will be automatically separated
                  </Form.Text>
                </Form.Group>
                
                <Button 
                  variant="primary" 
                  onClick={handleParseStudents}
                  disabled={!rawStudentText.trim()}
                  className="w-100"
                >
                  <FaPlus className="me-1" />
                  Add Students from Text
                </Button>
              </Card.Body>
            </Card>

            {/* Action Buttons */}
            <div className="d-grid gap-2">
              <Button 
                variant="outline-primary" 
                onClick={addManualStudent}
              >
                <FaPlus className="me-1" />
                Add Single Student
              </Button>
              
              {newStudents.length > 0 && (
                <Button 
                  variant="outline-danger" 
                  onClick={clearAllStudents}
                  size="sm"
                >
                  <FaTrash className="me-1" />
                  Clear All Students
                </Button>
              )}
            </div>

            {newStudents.length > 0 && (
              <div className="text-center mt-3">
                <Badge bg="info">
                  {newStudents.length} total
                </Badge>
                <Badge bg="success" className="ms-1">
                  {validStudentsCount} valid
                </Badge>
              </div>
            )}
          </div>
        </Col>

        {/* Column 2: Student Table */}
        <Col md={8}>
          <div className="mb-3">
            <h6 className="mb-3">
              Students to Add
              <Badge bg="primary" className="ms-2">{validStudentsCount}</Badge>
            </h6>

            <div style={{ height: '500px', overflowY: 'auto' }}>
              {newStudents.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FaUsers size={48} className="mb-3 opacity-50" />
                  <p>No students added yet</p>
                  <p>Use the import options on the left to add students</p>
                </div>
              ) : (
                <Table striped bordered hover size="sm">
                  <thead className="sticky-top bg-light">
                    <tr>
                      <th>Name</th>
                      <th>SEN</th>
                      <th>ESL</th>
                      <th>ASD</th>
                      <th>GDD</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newStudents.map(student => (
                      <tr key={student.id} className={!student.isValid ? 'table-warning' : ''}>
                        <td>
                          {student.id.startsWith('manual-') ? (
                            <Row>
                              <Col xs={6}>
                                <Form.Control
                                  size="sm"
                                  placeholder="First name"
                                  value={student.firstName}
                                  onChange={(e) => updateManualStudentName(student.id, e.target.value, student.lastName)}
                                />
                              </Col>
                              <Col xs={6}>
                                <Form.Control
                                  size="sm"
                                  placeholder="Last name"
                                  value={student.lastName}
                                  onChange={(e) => updateManualStudentName(student.id, student.firstName, e.target.value)}
                                />
                              </Col>
                            </Row>
                          ) : (
                            <div>
                              <div className="fw-medium">{student.displayName}</div>
                              {!student.isValid && (
                                <small className="text-danger">{student.validationError}</small>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={student.sen}
                            onChange={(e) => updateStudentField(student.id, 'sen', e.target.checked)}
                            title="Special Educational Needs"
                          />
                        </td>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={student.esl}
                            onChange={(e) => updateStudentField(student.id, 'esl', e.target.checked)}
                            title="English as Second Language"
                          />
                        </td>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={student.asd}
                            onChange={(e) => updateStudentField(student.id, 'asd', e.target.checked)}
                            title="Autism Spectrum Disorder"
                          />
                        </td>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={student.gdd}
                            onChange={(e) => updateStudentField(student.id, 'gdd', e.target.checked)}
                            title="Global Developmental Delay"
                          />
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeStudent(student.id)}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default NewStudentsSelection;